/**
 * Permission Constants for Frontend
 * Keep this in sync with backend permission structure
 */

// Permission modules with display names (organized by hierarchy)
export const PERMISSION_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "crm", label: "CRM" },
  { key: "banner", label: "Banner" },
  { key: "blogs", label: "Blogs" },
  { key: "studentLeaderBoard", label: "Student Leader Board" },
  { key: "tutorLeaderBoard", label: "Tutor Leader Board" },
  { key: "enquiry", label: "Enquiry" },
  { key: "demo", label: "Demo Management" },
  { key: "curriculum", label: "Curriculum" },
  { key: "slots", label: "Slots (Curriculum)" },
  { key: "subject", label: "Subject (Curriculum)" },
  { key: "class", label: "Class (Curriculum)" },
  { key: "board", label: "Board (Curriculum)" },
  { key: "report", label: "Report" },

  // People Module Children
  { key: "tutors", label: "Tutors (People)" },
  { key: "parents", label: "Parents (People)" },
  { key: "students", label: "Students (People)" },
  { key: "subAdmin", label: "Sub Admin (People)" },

  // Activities Module Children
  { key: "classMgt", label: "Class Management (Activities)" },
  { key: "supportTicket", label: "Support Ticket (Activities)" },
  { key: "ehc", label: "EHC (Activities)" },
  { key: "referral", label: "Referral (Activities)" },
  { key: "shortlistTutors", label: "Shortlist Tutors (Activities)" },
  { key: "attendance", label: "All Attendance (Activities)" },
  { key: "creditMapping", label: "Credit Mapping (Activities)" },
  { key: "tutorPayroll", label: "Tutor's Payroll (Activities)" },

  // Useful Corners Module Children
  { key: "question", label: "Question Management (Useful Corners)" },
  { key: "academic", label: "Academic (Useful Corners)" },
  { key: "product", label: "Product (Useful Corners)" },
  { key: "video", label: "All Videos (Useful Corners)" },

  // Communication Module Children
  { key: "notification", label: "Notification/Notice (Communication)" },
  { key: "message", label: "Messages (Communication)" },

  // Finance Module Children
  { key: "draftTutorSalary", label: "Draft Tutor Salary (Finance)" },
  { key: "allTransaction", label: "All Transactions (Finance)" },
  { key: "clientBalance", label: "Client Balance (Finance)" },
  { key: "raiseInvoice", label: "Raise Invoice (Finance)" },
  { key: "tutorPaymentCycle", label: "Tutor Payment Cycle (Finance)" },

  // Other modules
  { key: "subscription", label: "Subscription" },
  { key: "meeting", label: "Meeting" },
  { key: "review", label: "Review" },
  { key: "settings", label: "Settings" },
];

// Permission operations
export const PERMISSION_OPERATIONS = [
  { key: "create", label: "Create", color: "#4caf50" },
  { key: "get", label: "Get", color: "#2196f3" },
  { key: "update", label: "Update", color: "#ff9800" },
  { key: "delete", label: "Delete", color: "#f44336" },
];

/**
 * Initialize empty permissions for a module
 */
export const initializeModulePermissions = (moduleKey, existingPermissions = {}) => {
  if (!existingPermissions[moduleKey]) {
    return {
      create: false,
      get: false,
      update: false,
      delete: false,
    };
  }
  return existingPermissions[moduleKey];
};

/**
 * Check if user has permission
 */
export const checkPermission = (permissions, module, operation) => {
  return permissions?.[module]?.[operation] || false;
};

/**
 * Get all permissions (all operations enabled for all modules)
 */
export const getAllPermissions = () => {
  const allPerms = {};
  PERMISSION_MODULES.forEach((module) => {
    allPerms[module.key] = {
      create: true,
      get: true,
      update: true,
      delete: true,
    };
  });
  return allPerms;
};

/**
 * Check if module has any permission enabled
 */
export const hasAnyPermission = (permissions, moduleKey) => {
  // Handle array permissions (e.g., ["ALL"] for admins)
  if (Array.isArray(permissions)) {
    return permissions.includes("ALL") || permissions.includes(moduleKey);
  }

  // Handle object permissions (e.g., { class: { get: true, create: false } } for sub-admins)
  const perms = permissions[moduleKey];
  if (!perms) return false;
  return Object.values(perms).some((val) => val === true);
};

/**
 * Check if module has all permissions enabled
 */
export const hasAllPermissions = (permissions, moduleKey) => {
  const perms = permissions[moduleKey];
  if (!perms) return false;
  return Object.values(perms).every((val) => val === true);
};

/**
 * Count total permissions granted
 */
export const countTotalPermissions = (permissions) => {
  let count = 0;
  Object.values(permissions).forEach((modulePerms) => {
    Object.values(modulePerms).forEach((val) => {
      if (val === true) count++;
    });
  });
  return count;
};
